import React, {useCallback, useMemo, useState} from 'react';
import {Box, Grid, IconButton, InputAdornment, Popover, TextField, Tooltip, useTheme} from '@mui/material';
import {
    amber,
    blue,
    blueGrey,
    brown,
    cyan,
    deepOrange,
    deepPurple,
    green,
    grey,
    indigo,
    lightBlue,
    lightGreen,
    lime,
    orange,
    pink,
    purple,
    red,
    teal,
    yellow
} from '@mui/material/colors';
import {TextFieldProps} from "@mui/material/TextField/TextField";
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import {chipColors} from "../color-chip/ColorChip";
import {hashCode} from "../../util/StringUtil";


type ShadeType = (keyof typeof red) & number;
type ColorFamily = { name: string, color: Record<ShadeType, string> };

const shades: ShadeType[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

const colorFamilies: ColorFamily[] = [
    {name: 'Indigo', color: indigo},
    {name: 'Deep Purple', color: deepPurple},
    {name: 'Purple', color: purple},
    {name: 'Pink', color: pink},
    {name: 'Red', color: red},
    {name: 'Deep Orange', color: deepOrange},
    {name: 'Orange', color: orange},
    {name: 'Amber', color: amber},
    {name: 'Yellow', color: yellow},
    {name: 'Lime', color: lime},
    {name: 'Light Green', color: lightGreen},
    {name: 'Green', color: green},
    {name: 'Teal', color: teal},
    {name: 'Cyan', color: cyan},
    {name: 'Light Blue', color: lightBlue},
    {name: 'Blue', color: blue},
    {name: 'Blue Grey', color: blueGrey},
    {name: 'Brown', color: brown},
    {name: 'Grey', color: grey},
];

export function randomColor() {
    function randomElement<T>(array: T[]): T {
        return array[Math.floor(Math.random() * array.length)];
    }
    const randomColorFamily = randomElement(colorFamilies);
    const randomShade = randomElement(shades);
    return randomColorFamily.color[randomShade];
}

export function hashColor(seed: string) {
    const hash = hashCode(seed);

    // Pick color family and shade based on hash
    const colorFamilyIndex = hash % colorFamilies.length;
    const shadeIndex = Math.floor(hash / colorFamilies.length) % shades.length;

    return colorFamilies[colorFamilyIndex].color[shades[shadeIndex]];
}

function ColorPreview({color}: { color: string }) {
    return (
        <Box
            sx={{
                width: 32,
                height: 32,
                padding: '5px',
                bgcolor: color,
                borderRadius: 1,
                border: '1px solid rgba(0, 0, 0, 0.23)',
            }}
        >
            <CheckIcon fontSize="small" sx={{color: theme => theme.palette.getContrastText(color)}}/>
        </Box>
    )
}


function ColorBlock({value, onClick, colorFamily, shade}: {
    value: string,
    onClick: (color: string) => void,
    colorFamily: ColorFamily,
    shade: ShadeType,
}) {

    const theme = useTheme();

    const color = useMemo(
        () => colorFamily.color[shade],
        [colorFamily, shade]
    );

    const handleClick = useCallback(
        () => onClick(color),
        [color, onClick]
    );


    const styles = useMemo(() => ({
        cursor: "pointer",
        width: '32px',
        height: '32px',
        bgcolor: color,
        padding: '6px',
    }), [color]);

    return (
        <Tooltip title={`Color: ${colorFamily.name}, Shade: ${shade}`}>
            <Box
                sx={styles}
                onClick={handleClick}
            >
                {value.toLowerCase() === color.toLowerCase() && <CheckIcon fontSize="small" htmlColor={theme.palette.getContrastText(value)} />}
            </Box>
        </Tooltip>
    );
}

export default function ColorPicker({value, onChange, clearable = false, ...props}: {
    value?: string | null,
    onChange: (color: string) => void,
    clearable?: boolean,
} & Omit<TextFieldProps, 'onChange' | 'value'>) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const theme = useTheme();

    const color = value ? chipColors(theme, value).backgroundColor : '';

    const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        if (!props.disabled) setAnchorEl(event.currentTarget);
    }, [setAnchorEl, props.disabled]);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const handleColorFamilyClick = useCallback((color: string) => {
        onChange(color);
        handleClose();
    }, [onChange, handleClose]);

    const handleClear = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onChange('');
        handleClose();
    }, [onChange, handleClose]);

    const open = Boolean(anchorEl);

    return (
        <>
            <TextField
                {...props}
                value={color}
                onClick={handleOpen}
                slotProps={{
                    ...(props.slotProps || {}),
                    input: {
                        readOnly: true,
                        startAdornment: color ? (
                            <InputAdornment position="start">
                                <ColorPreview color={color}/>
                            </InputAdornment>
                        ) : undefined,
                        endAdornment: clearable && color && !props.disabled ? (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="Clear"
                                    title="Clear"
                                    size="small"
                                    onMouseDown={event => event.preventDefault()}
                                    onClick={handleClear}
                                >
                                    <ClearIcon fontSize="small"/>
                                </IconButton>
                            </InputAdornment>
                        ) : undefined,
                        ...(props.slotProps?.input || {})
                    },
                }}
            />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Box sx={{m: 2, p: 2, width: 32 * colorFamilies.length + 16}}>
                    <Grid container>
                        {shades.flatMap(shade =>
                            colorFamilies.map(colorFamily => (
                                <Grid key={`${colorFamily.name}-${shade}`} size={12/colorFamilies.length}>
                                    <ColorBlock
                                        value={color}
                                        colorFamily={colorFamily}
                                        shade={shade}
                                        onClick={handleColorFamilyClick}
                                    />
                                </Grid>
                            ))
                        )}
                    </Grid>
                </Box>
            </Popover>
        </>
    );
}
